import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, api, type Overview } from '../api'
import { Card, Chip, Info, Loading } from '../components'
import { vocabulary } from '../vocabulary'

const home = vocabulary.home
const terms = vocabulary.terms
const glossary = vocabulary.glossary

/**
 * The state of the organization at a glance, and — while it is new — the two ways to start.
 *
 * Two label counts, because the two sides of a shipment are usually two organizations: what this
 * organization's servers published, and what other people's servers sealed to its devices. A
 * shipper sees one number and a zero; a carrier sees the mirror.
 */
export default function Home({ accountId, organizationName }: {
  accountId: string
  organizationName: string
}) {
  const [data, setData] = useState<Overview | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setData(null)
    setError(null)
    api.overview(accountId)
      .then((overview) => live && setData(overview))
      .catch((failure) => live && setError(
        failure instanceof ApiError ? failure.message : home.loadError))
    return () => { live = false }
  }, [accountId])

  if (error) return <p className="banner stop">{error}</p>
  if (!data) return <Loading rows={4} />

  const sending = data.setup.server_connected
  const reading = data.setup.admin_device_paired
  const started = sending || reading

  return (
    <>
      <div className="stack" style={{ gap: 3 }}>
        <h1>{organizationName}</h1>
        <p className="small muted">{home.subhead}</p>
      </div>

      <div className="tiles">
        <div className="tile">
          <span className="n">{data.labels_published.total.toLocaleString()}</span>
          <span className="k">{glossary.published.title} <Info term="published" /></span>
          <span className="sub">
            <span style={{ display: 'block' }}>{data.labels_published.active.toLocaleString()} {home.active}</span>
            <span style={{ display: 'block' }}>{data.labels_published.shredded.toLocaleString()} {home.shredded}</span>
          </span>
        </div>
        <div className="tile">
          <span className="n">{data.labels_received.total.toLocaleString()}</span>
          <span className="k">{glossary.received.title} <Info term="received" /></span>
          <span className="sub">
            <span style={{ display: 'block' }}>{data.labels_received.active.toLocaleString()} {home.active}</span>
            <span style={{ display: 'block' }}>{data.labels_received.shredded.toLocaleString()} {home.shredded}</span>
          </span>
        </div>
      </div>

      <div className="tiles three">
        <Link className="tile" to="../devices" style={{ textDecoration: 'none', color: 'inherit' }}>
          <span className="n">{data.admin_devices}</span>
          <span className="k">{terms.adminDevices} <Info term="admin" /></span>
        </Link>
        <Link className="tile" to="../devices" style={{ textDecoration: 'none', color: 'inherit' }}>
          <span className="n">{data.reader_devices}</span>
          <span className="k">{terms.readerDevices} <Info term="reader" /></span>
        </Link>
        <Link className="tile" to="../servers" style={{ textDecoration: 'none', color: 'inherit' }}>
          <span className="n">{data.servers}</span>
          <span className="k">{terms.senders} <Info term="server" /></span>
        </Link>
      </div>

      {(data.high_risk_devices > 0 || data.waiting_for_approval > 0) && (
        <Card
          title={home.attentionTitle}
          action={data.high_risk_devices > 0
            ? <Chip kind="stop">{data.high_risk_devices} {home.atHighRisk}</Chip>
            : <Chip kind="wait">{data.waiting_for_approval} {home.waitingChip}</Chip>}
          footer={data.high_risk_devices > 0 ? home.highRiskFooter : home.waitingFooter}
        >
          <div className="stack" style={{ gap: 10 }}>
            {data.high_risk_devices > 0 && (
              <div className="between">
                <p className="small">
                  {data.high_risk_devices === 1
                    ? home.highRiskOne
                    : `${data.high_risk_devices} ${home.highRiskMany}`}
                </p>
                <Link className="btn small" to="../devices">{home.reviewDevices}</Link>
              </div>
            )}
            {data.waiting_for_approval > 0 && (
              <div className="between">
                <p className="small">
                  {data.waiting_for_approval === 1
                    ? home.waitingOne
                    : `${data.waiting_for_approval} ${home.waitingMany}`}
                </p>
                <Link className="btn small" to="../devices">{home.viewDevices}</Link>
              </div>
            )}
          </div>
        </Card>
      )}

      {!(sending && reading) && (
        <Card title={home.getStarted} action={<span className="tiny faint">{home.doEither}</span>}>
          <div className="stack" style={{ gap: 14 }}>
            <div className="between wrap-row">
              <div className="stack" style={{ gap: 2 }}>
                <b className="small">{home.sendLabels}</b>
                <span className="tiny faint">
                  {sending ? home.senderConnected : home.connectPublisher}
                </span>
              </div>
              {!sending && <Link className="btn small" to="../servers">{home.connectSender}</Link>}
            </div>
            <div
              className="between wrap-row"
              style={{ borderTop: '1px solid var(--line)', paddingTop: 14 }}
            >
              <div className="stack" style={{ gap: 2 }}>
                <b className="small">{home.readLabels}</b>
                <span className="tiny faint">
                  {reading
                    ? data.setup.reader_device_invited
                      ? home.allSetUp
                      : home.adminPairedInvite
                    : home.pairThenInvite}
                </span>
              </div>
              <Link className="btn small" to="../devices">
                {reading ? home.inviteDevice : home.pairAdminDevice}
              </Link>
            </div>
          </div>
        </Card>
      )}

      {!started && (
        <p className="tiny faint">{home.notSureWhich}</p>
      )}
    </>
  )
}
